import { useEffect, useState } from 'react';
import {
    COLLABORATION_METHOD_OPTION_KEY,
    COLLABORATION_METHOD_SYNC,
    DESIGN_LIVE_REVIEW_METHOD,
    DESIGN_REQUEST_OPTION_KEY,
    STANDARD_REVIEW_METHOD,
    bugtracker,
    useIdentityContext,
} from '@99designs/design-services-common';
import { FullPageSpinner } from '@99designs/design-services-layouts';
import { useInVistaExperimentVariant } from '@99designs/experiments';
import {
    ServiceProductOptions,
    determineActualEsMpvID,
    determineActualProductKey,
    getRequestExperiments,
    useChatBriefFeatureFlag,
    useCreateBriefMutation,
    useCreateRequestMutation,
    useLogoTouchUpFeatureFlag,
    useNavigateWithLang,
    useWeddingExperiment,
} from '@99designs/feature-design-services-brief';
import { KeyValue } from '@99designs/graph-utils/types';
import { DS_BRIEF_MULTI_STEP_URL } from '@99designs/i18n';

const getQueryParamClosure =
    (query: URLSearchParams) =>
    (...keys: string[]): string[] => {
        const entries = Array.from(query.entries());
        const foundEntries: string[] = [];
        for (const key of keys) {
            const foundEntry = entries.find(([k]) => k.toLowerCase() === key.toLowerCase());
            if (foundEntry) {
                return foundEntries.concat(foundEntry[1]);
            }
        }
        return foundEntries;
    };

// Override logic only is relevant for the Logo feature gates when users are not enrolled.

function parseChatBriefOverride(value: string | undefined): boolean | undefined {
    if (value === 'true') {
        return true;
    }

    if (value === 'false') {
        return false;
    }

    return undefined;
}

function parseReportedIssues(value: string | undefined): string[] | undefined {
    const issues = (value ?? '')
        .split(',')
        .map((issue) => issue.trim())
        .filter((issue) => issue !== '');

    return issues.length > 0 ? issues : undefined;
}

export const Create: React.FC<{ locale: string }> = ({ locale }) => {
    const getQueryParam = getQueryParamClosure(new URLSearchParams(window.location.search));

    const mpvId = getQueryParam('mpvId').pop() ?? '';
    const esMpvID = getQueryParam('esmpvId').pop() ?? '';
    const workId = getQueryParam('workId').pop() ?? null;
    const options = getQueryParam('options', 'selectedOptions').pop() ?? null;
    const quantity = getQueryParam('quantity', 'qty').pop();
    const title = getQueryParam('title').pop() ?? undefined;
    const schemaId = getQueryParam('schemaId').pop() ?? undefined;
    const isTest = getQueryParam('isTest').pop() === 'true';
    const brief_experiments = getQueryParam('brief_experiments').pop() ?? undefined;
    const referenceProjects = getQueryParam('referenceProjects').pop() ?? undefined;
    const productKey = getQueryParam('productKey').pop() ?? '';
    const preferredDesigner = getQueryParam('preferredDesigner').pop() === 'true';
    const chatBrief = getQueryParam('chatBrief').pop();
    const reportedIssues = parseReportedIssues(getQueryParam('reportedIssues').pop());

    const serviceProductOptions = new ServiceProductOptions(
        getQueryParam('serviceselectedoptions').pop()
    );
    const designServiceType = serviceProductOptions.get(DESIGN_REQUEST_OPTION_KEY);
    const collaborationMethod = serviceProductOptions.get(COLLABORATION_METHOD_OPTION_KEY);
    const isDesignLive = collaborationMethod === COLLABORATION_METHOD_SYNC;

    // @deprecated: use {@link logomakerEntityId} and {@link logomakerEntitySig} instead
    const logomakerDesigns = getQueryParam('logomakerDesigns').pop() ?? undefined;
    const logomakerEntityId = getQueryParam('logomakerEntityId').pop() ?? undefined;
    const logomakerEntitySig = getQueryParam('logomakerEntitySig').pop() ?? undefined;

    const {
        isFeatureGateEnabled: isLogoTouchUpFeatureGateEnabled,
        isLoading: isLogoTouchUpLoading,
    } = useLogoTouchUpFeatureFlag(esMpvID);
    const { isFeatureGateEnabled: isChatBriefEnabledForUser, isLoading: isChatBriefLoading } =
        useChatBriefFeatureFlag(esMpvID, workId);

    const isChatBriefEnabled = parseChatBriefOverride(chatBrief) ?? isChatBriefEnabledForUser;

    let initFields: { [key: string]: string } = {};
    if (mpvId !== '') {
        initFields = { ...initFields, physicalProduct: mpvId };
    }

    if (collaborationMethod) {
        initFields = {
            ...initFields,
            requestType: isDesignLive ? DESIGN_LIVE_REVIEW_METHOD : STANDARD_REVIEW_METHOD,
        };
    }
    if (workId) {
        initFields = { ...initFields, workId };
    }
    if (title) {
        initFields = { ...initFields, title };
    }
    if (designServiceType) {
        initFields = { ...initFields, designServiceType };
    }

    // TODO: remove, these no longer need to go into "initFields" when all logomaker goes through
    //       the new (circa late 2025) product flow

    if (logomakerDesigns) {
        initFields = { ...initFields, logomakerDesigns };
    }

    if (isLogoTouchUpLoading || isChatBriefLoading) {
        return null;
    }

    const actualEsMpvID = determineActualEsMpvID(esMpvID, isLogoTouchUpFeatureGateEnabled);
    const actualProductKey = determineActualProductKey(
        esMpvID,
        productKey,
        isLogoTouchUpFeatureGateEnabled
    );

    // TODO: isRnBEnabled is legacy. In the near future, product keys will not be sent in favour
    //       of the ES MPV ID.

    const isRnBEnabled = ![
        'logo-essential',
        'logo-touch-up-&-completion-service',
        'essential-stand-up-pouch-package',
    ].includes(actualProductKey);

    return (
        <ClientNewRequest
            mpvId={mpvId}
            experiments={{
                schemaId,
                referenceProjectsEnabled: referenceProjects === 'true' ? true : undefined,
                isChatBriefEnabled,
            }}
            params={
                isRnBEnabled
                    ? {
                          type: 'create_request',
                          esmpvId: actualEsMpvID,
                          workId: workId,
                          withDesignLive: isDesignLive,
                          logomakerDesigns: logomakerDesigns,
                          logomakerEntityId: logomakerEntityId,
                          logomakerEntitySig: logomakerEntitySig,
                          title: title,
                          options: options,
                          locale: locale,
                          quantity: quantity ? Number(quantity) : null,
                          serviceProductOptions: serviceProductOptions.asArray,
                          isTest: isTest,
                          reportedIssues: reportedIssues,
                      }
                    : {
                          type: 'create_brief',
                          productKey:
                              actualProductKey ||
                              (() => {
                                  throw new Error('productKey is required');
                              })(),
                          fields: initFields,
                      }
            }
            router={{
                query: {
                    brief_experiments,
                },
            }}
            preferredDesigner={preferredDesigner}
        />
    );
};

export interface ClientNewRequestProps {
    params:
        | {
              type: 'create_request';
              esmpvId: string;
              workId: string | null;
              options: string | null;
              quantity: number | null;
              locale: string;
              withDesignLive?: boolean;
              title?: string;
              // @deprecated: use {@link logomakerEntityId} and {@link logomakerEntitySig} instead
              logomakerDesigns?: string;
              logomakerEntityId?: string;
              logomakerEntitySig?: string;
              serviceProductOptions?: KeyValue[];
              isTest?: boolean;
              reportedIssues?: string[];
          }
        | {
              type: 'create_brief';
              productKey: string;
              fields: {
                  [key: string]: string;
              };
          };
    mpvId: string | null;
    experiments: {
        schemaId?: string;
        referenceProjectsEnabled: boolean | undefined;
        isChatBriefEnabled: boolean;
    };
    router: {
        query: { [key: string]: string | string[] | undefined };
    };
    preferredDesigner?: boolean;
}

export function ClientNewRequest({
    mpvId,
    experiments: { schemaId, referenceProjectsEnabled, isChatBriefEnabled },
    params,
    preferredDesigner,
}: ClientNewRequestProps) {
    const navigate = useNavigateWithLang();
    const [called, setCalled] = useState(false);
    const { authorizationHeader } = useIdentityContext();
    const [createBrief] = useCreateBriefMutation({
        onCompleted: async (data) => {
            navigate(`${DS_BRIEF_MULTI_STEP_URL}/${data.brief.id}`);
        },
        onError: (e) => {
            bugtracker.notify(e);
            navigate(DS_BRIEF_MULTI_STEP_URL + '/error');
        },
        errorPolicy: 'all',
    });
    const [createRequest] = useCreateRequestMutation({
        onCompleted: (data) => {
            const currentUrl = data.createRequest.facts?.currentUrl;
            if (currentUrl) {
                return navigate(isChatBriefEnabled ? `${currentUrl}/chat` : currentUrl, {
                    replace: true,
                    state: { createSearchParams: window.location.search },
                });
            }
            return navigate(DS_BRIEF_MULTI_STEP_URL + '/error');
        },
        onError: (e) => {
            bugtracker.notify(e);
            navigate(DS_BRIEF_MULTI_STEP_URL + '/error');
        },
        errorPolicy: 'all',
    });

    const isWeddingBriefEnabled = useWeddingExperiment(mpvId);
    const inIbpoTestVariant = useInVistaExperimentVariant('ds_sdp_ibpo');

    useEffect(() => {
        if (authorizationHeader === '') {
            return;
        }
        if (called) {
            return;
        }
        setCalled(true);
        if (params.type === 'create_brief') {
            createBrief({
                variables: {
                    slug: params.productKey,
                    fields: params.fields,
                },
            });
            return;
        }

        const isTest = params.isTest === true ? true : undefined;

        void createRequest({
            variables: {
                input: {
                    facts: {
                        locale: params.locale,
                        expertService: {
                            mpvId: params.esmpvId,
                            withDesignLive: params.withDesignLive ?? false,
                        },
                    },
                    optionalFacts: {
                        workId: params.workId ?? undefined,
                        experiments: getRequestExperiments(
                            isWeddingBriefEnabled,
                            schemaId,
                            referenceProjectsEnabled,
                            isChatBriefEnabled
                        ),
                        title: params.title,
                        logomakerDesigns: params.logomakerDesigns,
                        logomakerEntityId: params.logomakerEntityId,
                        logomakerEntitySig: params.logomakerEntitySig,
                        printOutcome: {
                            mpvId,
                            options: params.options ? { encoded: params.options } : undefined,
                            quantity: params.quantity ?? undefined,
                        },
                        serviceProductOptions: params.serviceProductOptions,
                        isTest: isTest,
                        customerDocument: params.reportedIssues
                            ? { reportedIssues: params.reportedIssues }
                            : undefined,
                    },
                },
                usePreferredDesigner: preferredDesigner,
                experimentVariants:
                    inIbpoTestVariant('test') === true ? { ds_sdp_ibpo: 'test' } : undefined,
            },
        });
    }, [
        createRequest,
        mpvId,
        authorizationHeader,
        createBrief,
        called,
        params,
        preferredDesigner,
        isWeddingBriefEnabled,
        schemaId,
        referenceProjectsEnabled,
        isChatBriefEnabled,
        inIbpoTestVariant,
    ]);

    return <FullPageSpinner />;
}
